import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, getMac, setMac, setDeviceKey } from "@/lib/api";
import PlaylistForm from "@/components/PlaylistForm";
import { toast } from "sonner";
import { MonitorPlay, Copy, Clock, ShieldCheck, PlayCircle, Loader2, Download, Settings2 } from "lucide-react";

export default function Landing() {
  const [device, setDevice] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    let mac = getMac();
    const { data } = await api.post("/device/register", mac ? { mac } : {});
    setMac(data.mac);
    setDeviceKey(data.device_key);
    setDevice(data);
  };

  useEffect(() => { load(); }, []);

  const copy = (val, label) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  };

  if (!device) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
    </div>
  );

  const hasPlaylist = device.playlists?.length > 0;

  return (
    <div className="min-h-screen relative">
      <img src="https://images.pexels.com/photos/3137890/pexels-photo-3137890.jpeg"
        alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/80" />

      <div className="relative z-10">
        <header className="flex items-center justify-between px-6 md:px-12 py-6">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-red-600"><MonitorPlay className="w-5 h-5 text-white" /></div>
            <span className="font-head text-xl font-bold tracking-tight">IBO<span className="text-red-500">STREAM</span></span>
          </div>
          <div className="flex items-center gap-5 text-sm text-zinc-400">
            <button data-testid="download-link" onClick={() => navigate("/download")} className="hover:text-white transition-colors flex items-center gap-1.5"><Download className="w-4 h-4" /> Get the App</button>
            <button data-testid="manage-link" onClick={() => navigate("/manage")} className="hover:text-white transition-colors flex items-center gap-1.5"><Settings2 className="w-4 h-4" /> Manage Device</button>
            <button data-testid="admin-link" onClick={() => navigate("/admin")} className="hover:text-white transition-colors">Admin</button>
          </div>
        </header>

        <div className="px-6 md:px-12 grid lg:grid-cols-2 gap-10 items-start max-w-7xl mx-auto pt-8 pb-20">
          <div className="fade-up">
            <p className="text-xs uppercase tracking-[0.25em] text-red-500 font-semibold mb-4">IPTV Player</p>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]">
              Watch everything in one place
            </h1>
            <p className="text-zinc-400 text-lg mt-5 max-w-md leading-relaxed">
              Live TV, movies and series via your M3U or Xtream Codes playlist. Activate your device and enjoy.
            </p>

            <div className="mt-8 glass rounded-2xl p-6 max-w-md">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Device MAC Address</span>
                <span className={`flex items-center gap-1.5 text-xs font-semibold ${device.active ? "text-emerald-400" : "text-red-500"}`}>
                  <span className={`w-2 h-2 rounded-full ${device.active ? "bg-emerald-400" : "bg-red-500"}`} />
                  {device.status === "active" ? "Active" : device.active ? `Trial · ${device.days_left} days` : "Expired"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <code data-testid="device-mac" className="font-mono-data text-2xl tracking-wider text-zinc-100 flex-1">{device.mac}</code>
                <button data-testid="copy-mac-btn" onClick={() => copy(device.mac, "MAC")} className="p-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between text-sm">
                <span className="text-zinc-500">Device Key: <code className="font-mono-data text-zinc-300" data-testid="device-key">{device.device_key}</code></span>
                <span className="flex items-center gap-1.5 text-zinc-400"><Clock className="w-3.5 h-3.5" /> 7-day free trial</span>
              </div>
            </div>

            {hasPlaylist && (
              <button data-testid="open-player-btn" onClick={() => navigate("/player")}
                className="mt-6 flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-red-600 hover:bg-red-500 text-white font-semibold transition-colors">
                <PlayCircle className="w-5 h-5" /> Open Player
              </button>
            )}
          </div>

          <div className="glass rounded-2xl p-8 fade-up lg:mt-16">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
              <h2 className="font-head text-2xl font-semibold">Add Playlist</h2>
            </div>
            <p className="text-zinc-500 text-sm mb-6">Enter an M3U link or Xtream Codes details for this device.</p>
            <PlaylistForm onDone={() => { load(); navigate("/player"); }} />
          </div>
        </div>
      </div>
    </div>
  );
}
